import { logger } from '@heidi/runtime/logger';
import {
  NOTE_GENERATION_PHASE_EVENT,
  NOTE_SUGGESTIONS_EVENT,
  noteGenerationPhaseFrameSchema,
  noteSuggestionsFrameSchema,
  type NoteGenerationPhaseFrame,
  type NoteSuggestionsFrame,
} from './types';

export const isNoteSuggestionsEvent = (event: string | undefined): boolean =>
  event === NOTE_SUGGESTIONS_EVENT;

export const isNoteGenerationPhaseEvent = (event: string | undefined): boolean =>
  event === NOTE_GENERATION_PHASE_EVENT;

export const parseNoteSuggestionsFrame = (data: string): NoteSuggestionsFrame | null => {
  let raw: unknown;
  try {
    raw = JSON.parse(data);
  } catch (error) {
    logger.error('[note_suggestions] failed to JSON.parse suggestion frame', {
      error,
    });
    return null;
  }

  const result = noteSuggestionsFrameSchema.safeParse(raw);
  if (!result.success) {
    logger.error('[note_suggestions] suggestion frame failed schema validation', {
      issues: result.error.issues,
    });
    return null;
  }
  return result.data;
};

/**
 * Single entry point for the `note_suggestions` SSE frame, shared by every
 * stream path (web v2.2, tauri v2.1). Returns true if the event was consumed
 * (whether or not the handler ran), so callers can early-return without
 * falling through to their default text-chunk parsing.
 */
export function parseNoteGenerationPhaseFrame(
  data: string
): NoteGenerationPhaseFrame | null {
  let raw: unknown;
  try {
    raw = JSON.parse(data);
  } catch (error) {
    logger.error('[note_generation_phase] failed to JSON.parse phase frame', { error });
    return null;
  }

  const result = noteGenerationPhaseFrameSchema.safeParse(raw);
  if (!result.success) {
    logger.error('[note_generation_phase] phase frame failed schema validation', {
      issues: result.error.issues,
    });
    return null;
  }
  return result.data;
}

/**
 * Consumes every named personalisation phase event, including malformed or
 * unsupported payloads. This is deliberately fail-closed so a known control
 * event can never fall through to the v2.1 text-chunk parser and abort note
 * generation.
 */
export function tryHandleNoteGenerationPhaseEvent(
  ev: { event?: string; data: string },
  onNoteGenerationPhase: ((frame: NoteGenerationPhaseFrame) => void) | undefined,
  logPrefix: string,
  context?: Record<string, unknown>
): boolean {
  if (!isNoteGenerationPhaseEvent(ev.event)) return false;
  logger.info(`${logPrefix} received note_generation_phase frame`, {
    ...context,
    rawLength: ev.data?.length ?? 0,
  });
  const frame = parseNoteGenerationPhaseFrame(ev.data);
  if (!frame) return true;
  try {
    onNoteGenerationPhase?.(frame);
  } catch (error) {
    logger.error(`${logPrefix} onNoteGenerationPhase handler error`, error);
  }
  return true;
}

export function tryHandleNoteSuggestionsEvent(
  ev: { event?: string; data: string },
  onNoteSuggestions: ((frame: NoteSuggestionsFrame) => void) | undefined,
  logPrefix: string,
  context?: Record<string, unknown>
): boolean {
  if (!isNoteSuggestionsEvent(ev.event)) return false;
  logger.info(`${logPrefix} received note_suggestions frame`, {
    ...context,
    rawLength: ev.data?.length ?? 0,
  });
  const frame = parseNoteSuggestionsFrame(ev.data);
  if (!frame) return true;
  logger.info(`${logPrefix} parsed note_suggestions frame`, {
    ...context,
    strategyCount: frame.strategies.length,
    applicationCount: frame.strategies.reduce((n, s) => n + s.applications.length, 0),
  });
  try {
    onNoteSuggestions?.(frame);
  } catch (error) {
    logger.error(`${logPrefix} onNoteSuggestions handler error`, error);
  }
  return true;
}
